import { useMutation, useQueryClient } from '@tanstack/react-query';

import APIGateway from '@/api';
import { agentsKeys } from '@/hooks/queries/useAgents';
import { byoaAgentsKeys } from '@/hooks/queries/useByoaAgents';
import { AgentScaleArgs } from '@/types/agent';

type UseAgentDeleteArgs = {
  agentId: string;
};

type UseAgentMakePublicArgs = {
  agentId: string;
};

type UseAgentMakePrivateArgs = {
  agentId: string;
};

type UseAgentRestartArgs = {
  agentId: string;
};

type UseAgentTurnOffArgs = {
  agentId: string;
};

type UseAgentTurnOnArgs = {
  agentId: string;
};

export function useAgentDelete() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( { agentId }: UseAgentDeleteArgs ) => {
      return await APIGateway.agents.agentDelete( { agentId } );
    },
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: agentsKeys.all } );
    }
  } );
}

export function useByoaAgentDelete() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( { agentId }: UseAgentDeleteArgs ) => {
      return await APIGateway.byoaAgents.byoaAgentDelete( agentId );
    },
    onSuccess: () => {
      queryClient.invalidateQueries( { queryKey: agentsKeys.all } );
      queryClient.invalidateQueries( { queryKey: byoaAgentsKeys.all } );
    }
  } );
}

export function useAgentMakePublic() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( { agentId }: UseAgentMakePublicArgs ) => {
      return await APIGateway.agents.agentUpdate( { agentId, isPublic: true } );
    },
    onSuccess: ( _data, variables ) => {
      queryClient.invalidateQueries( { queryKey: agentsKeys.detail( variables.agentId ) } );
      queryClient.invalidateQueries( { queryKey: agentsKeys.list() } );
    }
  } );
}

export function useAgentMakePrivate() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( { agentId }: UseAgentMakePrivateArgs ) => {
      return await APIGateway.agents.agentUpdate( { agentId, isPublic: false } );
    },
    onSuccess: ( _data, variables ) => {
      queryClient.invalidateQueries( { queryKey: agentsKeys.detail( variables.agentId ) } );
      queryClient.invalidateQueries( { queryKey: agentsKeys.list() } );
    }
  } );
}

export function useAgentScale() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( args: AgentScaleArgs ) => {
      return await APIGateway.agents.agentScale( args );
    },
    onSuccess: ( _data, variables ) => {
      queryClient.invalidateQueries( { queryKey: agentsKeys.detail( variables.agentId ) } );
    }
  } );
}

export function useAgentRestart() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( { agentId }: UseAgentRestartArgs ) => {
      return await APIGateway.agents.agentRestart( { agentId } );
    },
    onSuccess: ( _data, variables ) => {
      queryClient.invalidateQueries( { queryKey: agentsKeys.detail( variables.agentId ) } );
    }
  } );
}

export function useAgentTurnOn() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( { agentId }: UseAgentTurnOnArgs ) => {
      return await APIGateway.agents.agentTurnOn( { agentId } );
    },
    onSuccess: ( _data, variables ) => {
      queryClient.invalidateQueries( { queryKey: agentsKeys.detail( variables.agentId ) } );
    }
  } );
}

export function useAgentTurnOff() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( { agentId }: UseAgentTurnOffArgs ) => {
      return await APIGateway.agents.agentTurnOff( { agentId } );
    },
    onSuccess: ( _data, variables ) => {
      queryClient.invalidateQueries( { queryKey: agentsKeys.detail( variables.agentId ) } );
    }
  } );
}
